import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ContactSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    currentSystem: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);

  const translations = {
    en: {
      title: "Get Your Free Consultation",
      subtitle: "Schedule a personalized demo and discover how our ERP can transform your aluminum manufacturing business",
      form: {
        name: "Full Name",
        email: "Business Email",
        phone: "Phone Number",
        company: "Company Name",
        employees: "Number of Employees",
        currentSystem: "Current System (if any)",
        timeline: "Implementation Timeline",
        message: "Tell us about your specific requirements",
        submit: "Schedule Free Consultation"
      },
      placeholders: {
        name: "Enter your full name",
        email: "your.email@company.com",
        phone: "+91 98765 43210",
        company: "Your company name",
        employees: "Select range",
        currentSystem: "e.g., Tally, Excel, SAP",
        timeline: "Select timeline",
        message: "Describe your current challenges and requirements..."
      },
      options: {
        employees: [
          "10-25 employees",
          "26-50 employees", 
          "51-100 employees",
          "101-250 employees",
          "250+ employees"
        ],
        timeline: [
          "Immediate (within 1 month)",
          "Short term (1-3 months)",
          "Medium term (3-6 months)",
          "Long term (6+ months)",
          "Just exploring options"
        ]
      },
      contactInfo: {
        title: "Get in Touch",
        phone: "+91 98765 43210",
        email: "sales@alumerp.com",
        address: "123 Industrial Area, Sector 15, Gurgaon, Haryana 122001",
        hours: "Mon-Sat: 9:00 AM - 7:00 PM IST"
      },
      availableSlots: "Available consultation slots:",
      urgentContact: "Need immediate assistance?",
      whatsapp: "WhatsApp Us",
      success: "Thank you! We\'ll contact you within 24 hours to schedule your consultation.",
      error: "Something went wrong. Please try again or contact us directly."
    },
    hi: {
      title: "अपना मुफ्त परामर्श प्राप्त करें",
      subtitle: "एक व्यक्तिगत डेमो शेड्यूल करें और जानें कि हमारा ERP आपके एल्यूमीनियम निर्माण व्यवसाय को कैसे बदल सकता है",
      form: {
        name: "पूरा नाम",
        email: "व्यावसायिक ईमेल",
        phone: "फोन नंबर",
        company: "कंपनी का नाम",
        employees: "कर्मचारियों की संख्या",
        currentSystem: "वर्तमान सिस्टम (यदि कोई है)",
        timeline: "कार्यान्वयन समयसीमा",
        message: "अपनी विशिष्ट आवश्यकताओं के बारे में बताएं",
        submit: "मुफ्त परामर्श शेड्यूल करें"
      },
      placeholders: {
        name: "अपना पूरा नाम दर्ज करें",
        email: "your.email@company.com",
        phone: "+91 98765 43210",
        company: "आपकी कंपनी का नाम",
        employees: "रेंज चुनें",
        currentSystem: "जैसे Tally, Excel, SAP",
        timeline: "समयसीमा चुनें",
        message: "अपनी वर्तमान चुनौतियों और आवश्यकताओं का वर्णन करें..."
      },
      options: {
        employees: [
          "10-25 कर्मचारी",
          "26-50 कर्मचारी",
          "51-100 कर्मचारी", 
          "101-250 कर्मचारी",
          "250+ कर्मचारी"
        ],
        timeline: [
          "तत्काल (1 महीने के भीतर)",
          "अल्पकालिक (1-3 महीने)",
          "मध्यम अवधि (3-6 महीने)",
          "दीर्घकालिक (6+ महीने)",
          "केवल विकल्प तलाश रहे हैं"
        ]
      },
      contactInfo: {
        title: "संपर्क में रहें",
        phone: "+91 98765 43210",
        email: "sales@alumerp.com",
        address: "123 इंडस्ट्रियल एरिया, सेक्टर 15, गुड़गांव, हरियाणा 122001",
        hours: "सोम-शनि: सुबह 9:00 - शाम 7:00 IST"
      },
      availableSlots: "उपलब्ध परामर्श स्लॉट:",
      urgentContact: "तत्काल सहायता चाहिए?",
      whatsapp: "हमें WhatsApp करें",
      success: "धन्यवाद! हम आपके परामर्श को शेड्यूल करने के लिए 24 घंटे के भीतर आपसे संपर्क करेंगे।",
      error: "कुछ गलत हुआ। कृपया पुनः प्रयास करें या सीधे हमसे संपर्क करें।"
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }

    // Generate available consultation slots
    const slots = [];
    const today = new Date();
    for (let i = 1; i <= 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      if (date.getDay() !== 0) { // Skip Sundays
        slots.push({
          date: date.toLocaleDateString('en-IN', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          }),
          time: i % 2 === 0 ? '2:00 PM' : '11:00 AM'
        });
      }
    }
    setAvailableSlots(slots.slice(0, 4));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          employees: '',
          currentSystem: '',
          timeline: '',
          message: ''
        });
        setSubmitStatus(null);
      }, 3000);
    }, 2000);
  };

  return (
    <section id="request-demo" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <Icon name="CheckCircle" size={64} color="var(--color-success)" className="mx-auto mb-6" />
                <h3 className="text-xl font-bold text-success mb-4">
                  {currentLanguage === 'en' ? 'Thank You!' : 'धन्यवाद!'}
                </h3>
                <p className="text-text-secondary">{t.success}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name and Email */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.name} *
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder={t.placeholders.name}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.email} *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder={t.placeholders.email}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Phone and Company */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.phone} *
                    </label>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder={t.placeholders.phone}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.company} *
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder={t.placeholders.company}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Employees and Current System */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.employees}
                    </label>
                    <select
                      value={formData.employees}
                      onChange={(e) => handleInputChange('employees', e.target.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">{t.placeholders.employees}</option>
                      {t.options.employees.map((option, index) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.currentSystem}
                    </label>
                    <Input
                      type="text"
                      value={formData.currentSystem}
                      onChange={(e) => handleInputChange('currentSystem', e.target.value)}
                      placeholder={t.placeholders.currentSystem}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t.form.timeline}
                  </label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">{t.placeholders.timeline}</option>
                    {t.options.timeline.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t.form.message}
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder={t.placeholders.message}
                    rows={4}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isSubmitting}
                  className="w-full bg-gradient-accent hover:bg-accent-600 shadow-cta"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  {t.form.submit}
                </Button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
              <h3 className="text-xl font-bold text-primary mb-6">
                {t.contactInfo.title}
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-lg">
                    <Icon name="Phone" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-medium text-primary">{t.contactInfo.phone}</p>
                    <p className="text-sm text-text-secondary">{t.contactInfo.hours}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-lg">
                    <Icon name="Mail" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-medium text-primary">{t.contactInfo.email}</p>
                    <p className="text-sm text-text-secondary">
                      {currentLanguage === 'en' ? 'Sales & Support' : 'बिक्री और सहायता'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary-50 rounded-lg">
                    <Icon name="MapPin" size={20} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="font-medium text-primary">
                      {currentLanguage === 'en' ? 'Head Office' : 'मुख्य कार्यालय'}
                    </p>
                    <p className="text-sm text-text-secondary">{t.contactInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Available Slots */}
            <div className="bg-surface-100 rounded-2xl p-8">
              <h3 className="text-lg font-semibold text-primary mb-4">
                {t.availableSlots}
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                {availableSlots.map((slot, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-3 text-center border border-border hover:border-primary transition-colors duration-200"
                  >
                    <div className="text-sm font-medium text-primary">{slot.date}</div>
                    <div className="text-xs text-text-secondary">{slot.time}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Urgent Contact */}
            <div className="bg-gradient-accent rounded-2xl p-8 text-white text-center">
              <Icon name="Zap" size={32} color="white" className="mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-4">{t.urgentContact}</h3>
              <Button
                variant="ghost"
                className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white border-white"
                iconName="MessageCircle"
                iconPosition="left"
              >
                {t.whatsapp}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;